import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TranslationProvider } from './context/TranslationContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { SocketProvider } from './contexts/SocketContext';
import { API_ENDPOINTS } from './utils/config';
import { useTranslation } from './context/TranslationContext';
import LandingPage from './components/LandingPage';
import LoginPage from './components/LoginPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import GeneralTerms from './pages/GeneralTerms';
import SsoCallbackPage from './pages/SsoCallbackPage';
import Header from './components/Header';
import StatsCards from './components/StatsCards';
import YearlyOverview from './components/YearlyOverview';
import SavingsChart from './components/SavingsChart';
import ProcurementSection from './components/ProcurementSection';
import AdminTable from './components/AdminTable';
import UserAdminProcurementTable from './components/UserAdminProcurementTable';
import AdminDashboard from './components/admin-v2/dashboard/AdminDashboard';
// OpenRFQsDashboard removed - replaced by AdminRFQDashboard
import RFQDetailDashboard from './components/admin-v2/dashboard/RFQDetailDashboard';
import RFQProductOverviewDashboard from './components/admin-v2/dashboard/RFQProductOverviewDashboard';
import RFQManualProductsDashboard from './components/admin-v2/dashboard/RFQManualProductsDashboard';
import RFQQuotesReviewDashboard from './components/admin-v2/dashboard/RFQQuotesReviewDashboard';
import RFQSupplierSearchDashboard from './components/admin-v2/dashboard/RFQSupplierSearchDashboard';
import RFQCommunicationDashboard from './components/admin-v2/dashboard/RFQCommunicationDashboard';
// User RFQ Components
import UserRFQManualProductsDashboard from './components/user-v2/dashboard/UserRFQManualProductsDashboard';
import UserRFQQuotesReviewDashboard from './components/user-v2/dashboard/UserRFQQuotesReviewDashboard';
import UserRFQSupplierSearchDashboard from './components/user-v2/dashboard/UserRFQSupplierSearchDashboard';
import UserRFQCommunicationDashboard from './components/user-v2/dashboard/UserRFQCommunicationDashboard';
import AdminRFQDashboard from './components/admin-v2/dashboard/AdminRFQDashboard';
import CompanyDashboard from './components/admin-v2/dashboard/CompanyDashboard';
import CompanyUsersDashboard from './components/admin-v2/dashboard/CompanyUsersDashboard';
import CompanyProductsDashboard from './components/admin-v2/dashboard/CompanyProductsDashboard';
import CompanyProductDetailsDashboard from './components/admin-v2/dashboard/CompanyProductDetailsDashboard';
import CompanySuppliersDashboard from './components/admin-v2/dashboard/CompanySuppliersDashboard';
import CompanyUserRightsDashboard from './components/admin-v2/dashboard/CompanyUserRightsDashboard';
import UserAdminRequestDetail from './pages/UserAdminRequestDetail';
import SentEmailsDashboard from './components/SentEmailsDashboard';
import RequestDetailPage from './components/RequestDetailPage';
import ResendTestPage from './components/ResendTestPage';
import ZeptoTestPage from './components/ZeptoTestPage';
// User V2 Components
import OpenRFQDashboard from './components/user-v2/dashboard/OpenRFQDashboard';
import ClosedRFQDashboard from './components/user-v2/dashboard/ClosedRFQDashboard';
import CreateRequestDashboard from './components/user-v2/dashboard/CreateRequestDashboard';
import ManualProductDashboard from './components/user-v2/dashboard/ManualProductDashboard';
import InboxDashboard from './components/user-v2/dashboard/InboxDashboard';
import SettingsDashboard from './components/user-v2/dashboard/SettingsDashboard';
import CartPage from './components/user-v2/pages/CartPage';
import NotificationModal from './components/NotificationModal';
import NewRequestModal from './components/NewRequestModal';
import TokenExpiredModal from './components/TokenExpiredModal';
import Footer from './components/Footer';
import { useApiInterceptor, setupGlobalFetchInterceptor } from './hooks/useApiInterceptor';
import { getDynamicSubItems } from './utils/getDynamicSubItems';
import './App.css';

function AppContent() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [userType, setUserType] = useState<'user' | 'admin' | 'useradmin' | null>(null);
  const [currentView, setCurrentView] = useState<'main' | 'sent-emails'>('main');
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionRequestId, setRevisionRequestId] = useState<string | null>(null);
  const [revisionRequestData, setRevisionRequestData] = useState<any>(null);
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(true);
  const [showLoginPage, setShowLoginPage] = useState(false);
  const [showTokenExpiredModal, setShowTokenExpiredModal] = useState(false);


  // Update userType when user changes
  React.useEffect(() => {
    if (user) {
      // Check both role and is_admin field for admin access
      const isAdmin = user.role === 'admin' || user.is_admin === true;

      let newUserType: 'user' | 'admin';
      if (isAdmin) {
        newUserType = 'admin';
      } else {
        newUserType = 'user';
      }

      setUserType(newUserType);
      // Save userType to localStorage for persistence
      localStorage.setItem('user_type', newUserType);
    } else {
      setUserType(null);
      localStorage.removeItem('user_type');
    }
  }, [user]);

  // Restore userType from localStorage on app start
  React.useEffect(() => {
    const savedUserType = localStorage.getItem('user_type') as 'user' | 'admin' | null;
    if (savedUserType && user) {
      setUserType(savedUserType);
    } else if (!user && savedUserType) {
      // User is null but userType exists, clear it
      localStorage.removeItem('user_type');
      setUserType(null);
    }
  }, [user]);

  // Setup token expired callback
  const handleTokenExpired = () => {
    console.log('🔒 Token expired callback triggered');
    // Otomatik logout yap
    logout();
    setUserType(null);
    setCurrentView('main');
    setShowLandingPage(true);
    setShowLoginPage(false);
    setShowTokenExpiredModal(true);
  };

  // Setup token expired callback - temporarily disabled
  // useApiInterceptor(handleTokenExpired);

  // useEffect(() => {
  //   // Setup global fetch interceptor
  //   setupGlobalFetchInterceptor();
  // }, []);

  const handleLogin = (type: 'user' | 'admin') => {
    setUserType(type);
    // Navigate to appropriate page after login
    if (type === 'admin') {
      navigate('/dashboard/customers');
    } else {
      navigate('/dashboard/user/inbox/open');
    }
  };

  const handleLandingPageLogin = () => {
    navigate('/signin');
  };

  const handleLogout = () => {
    logout();
    setUserType(null);
    localStorage.removeItem('user_type');
    setCurrentView('main');
    setShowLandingPage(true);
    setShowLoginPage(false);
    setShowTokenExpiredModal(false);
    navigate('/');
  };

  const handleTokenExpiredLogin = () => {
    setShowTokenExpiredModal(false);
    navigate('/signin');
  };

  const handleSentEmailsClick = () => {
    setCurrentView('sent-emails');
  };

  const handleNotificationsClick = () => {
    setShowNotificationModal(true);
  };

  const handleRevisionClick = async (requestId: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        alert(t('common.authentication_required'));
        return;
      }

      // Fetch the request data
      const response = await fetch(`${API_ENDPOINTS.REQUEST_DETAIL(requestId)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        setRevisionRequestData(data.data);
        setRevisionRequestId(requestId);
        setShowRevisionModal(true);
      } else {
        alert(t('common.failed_fetch_requests'));
      }
    } catch (error) {
      console.error('Error fetching request data:', error);
      alert(t('common.failed_fetch_requests'));
    }
  };

  const handleBackToMain = () => {
    setCurrentView('main');
  };

  const handleNewRequestClick = () => {
    setShowNewRequestModal(true);
  };

  const handleCloseNewRequestModal = () => {
    setShowNewRequestModal(false);
  };

  const handleSubmitNewRequest = async (formData: any) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        alert(t('common.authentication_required'));
        return;
      }

      const response = await fetch(API_ENDPOINTS.REQUESTS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        alert(t('common.request_created_successfully'));
        handleCloseNewRequestModal();
        // Optionally refresh the page or update the data
        window.location.reload();
      } else {
        alert(data.message || t('common.failed_create_request'));
      }
    } catch (error) {
      console.error('Error creating request:', error);
      alert(t('common.failed_create_request'));
    }
  };

  const handleRevisionModalClose = () => {
    setShowRevisionModal(false);
    setRevisionRequestId(null);
    setRevisionRequestData(null);
  };

  const handleRevisionSubmit = async (formData: any) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token || !revisionRequestId) {
        alert(t('common.request_id_missing'));
        return;
      }

      const response = await fetch(API_ENDPOINTS.UPDATE_REVISION(revisionRequestId), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        alert(t('common.request_updated_successfully'));
        handleRevisionModalClose();
        // Refresh notifications
        setShowNotificationModal(true);
      } else {
        alert(data.message || t('common.failed_update_request'));
      }
    } catch (error) {
      console.error('Error updating request:', error);
      alert(t('common.failed_update_request'));
    }
  };

  if (isLoading) {
    return <div>{t('common.loading')}</div>;
  }

  // Handle routing based on URL and authentication status
  if (!isAuthenticated) {
    if (location.pathname === '/signin') {
      return <LoginPage onLogin={handleLogin} onBack={() => navigate('/')} />;
    }
    return <LandingPage onLogin={handleLandingPageLogin} />;
  }

  // If authenticated, redirect to appropriate page if on wrong URL
  if (isAuthenticated && userType) {
    if (location.pathname === '/signin') {
      // Redirect to appropriate dashboard
      if (userType === 'admin') {
        navigate('/dashboard/customers');
      } else {
        navigate('/dashboard/user/inbox/open');
      }
      return null;
    }
    // Redirect "/" to appropriate dashboard based on user type
    if (location.pathname === '/') {
      if (userType === 'admin') {
        navigate('/dashboard/customers');
      } else {
        navigate('/dashboard/user/inbox/open');
      }
      return null;
    }
  }

  // Handle hard refresh case - if we have userType in localStorage but userType state is null
  if (isAuthenticated && !userType) {
    const savedUserType = localStorage.getItem('user_type') as 'user' | 'admin' | null;
    if (savedUserType) {
      // UserType exists in localStorage, wait for user state to be restored
      if (location.pathname === '/signin') {
        return <div>{t('common.loading')}</div>;
      }
      // Redirect "/" to appropriate dashboard if userType is saved in localStorage
      if (location.pathname === '/') {
        if (savedUserType === 'admin') {
          navigate('/dashboard/customers');
        } else {
          navigate('/dashboard/user/inbox/open');
        }
        return null;
      }
    }
  }

  // Handle /dashboard route - both admin and useradmin should see the same page
  if (isAuthenticated && (location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/'))) {
    // Check localStorage if userType state is not available yet
    const savedUserType = localStorage.getItem('user_type') as 'user' | 'admin' | null;
    const currentUserType = userType || savedUserType;

    // User role redirection is now handled in the render section below

    // Redirect /dashboard to /dashboard/customers only for admin and useradmin
    if (location.pathname === '/dashboard' && currentUserType === 'admin') {
      navigate('/dashboard/customers');
      return null;
    }
  }

  return (
    <div className="App">
      {/* Hide header for dashboard routes */}
      {!(location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/')) && (
        <Header
          onLogout={handleLogout}
          userType={userType}
          onSentEmailsClick={userType === 'admin' ? handleSentEmailsClick : undefined}
          onNotificationsClick={handleNotificationsClick}
          onNewRequestClick={userType === 'user' ? handleNewRequestClick : undefined}
        />
      )}
      {/* V2 Dashboard Routes - No old wrapper */}
      {location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/') ? (
        <>
          {(userType === 'admin' || localStorage.getItem('user_type') === 'admin') ? (
            <>
              {/* Admin-specific dynamic routes - check BEFORE AdminDashboard */}
              {location.pathname.startsWith('/dashboard/companies/') && location.pathname !== '/dashboard/companies' && location.pathname !== '/dashboard/customers' ? (
                // Route to specific company sub-dashboards
                location.pathname.endsWith('/users') ? (
                  <CompanyUsersDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management']
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.endsWith('/products') ? (
                  <CompanyProductsDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management']
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.endsWith('/product-details') ? (
                  <CompanyProductDetailsDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management']
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.endsWith('/suppliers') ? (
                  <CompanySuppliersDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management']
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.endsWith('/user-rights') ? (
                  <CompanyUserRightsDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management']
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : (
                  <CompanyDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management']
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                )
              ) : location.pathname.startsWith('/dashboard/rfqs/open') && !location.pathname.match(/\/dashboard\/rfqs\/open\/[^/]+/) ? (
                // Open RFQs Dashboard (new inbox-style with chat)
                <AdminRFQDashboard
                  user={{
                    id: user?._id || '',
                    name: user?.name || 'Admin',
                    email: user?.email || '',
                    role: 'admin',
                    permissions: ['company_management']
                  }}
                  currentRoute={location.pathname}
                  onNavigate={(route: string) => navigate(route)}
                  onLogout={handleLogout}
                  phaseFilter="open"
                />
              ) : location.pathname.startsWith('/dashboard/rfqs/finalized') ? (
                // Finalized RFQs Dashboard (new inbox-style with chat)
                <AdminRFQDashboard
                  user={{
                    id: user?._id || '',
                    name: user?.name || 'Admin',
                    email: user?.email || '',
                    role: 'admin',
                    permissions: ['company_management']
                  }}
                  currentRoute={location.pathname}
                  onNavigate={(route: string) => navigate(route)}
                  onLogout={handleLogout}
                  phaseFilter="finalized"
                />
              ) : location.pathname.startsWith('/dashboard/rfqs/open/') && location.pathname !== '/dashboard/rfqs/open' ? (
                // RFQ Detail sub-routes
                location.pathname.match(/\/dashboard\/rfqs\/open\/[^/]+\/manual-products(?:\/[^/]+)?$/) ? (
                  <RFQManualProductsDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management']
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.match(/\/dashboard\/rfqs\/open\/[^/]+\/quotes-review$/) ? (
                  <RFQQuotesReviewDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management']
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.match(/\/dashboard\/rfqs\/open\/[^/]+\/products$/) ? (
                  <RFQProductOverviewDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management']
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.match(/\/dashboard\/rfqs\/open\/[^/]+\/communication(?:\/[^/]+)?$/) ? (
                  <RFQCommunicationDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management']
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.match(/\/dashboard\/rfqs\/open\/[^/]+\/suppliers/) ? (
                  <RFQSupplierSearchDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management']
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : (
                  <RFQDetailDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management']
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                )
              ) : (
                <AdminDashboard
                  user={{
                    id: user?._id || '',
                    role: 'admin',
                    name: user?.name || 'Admin',
                    email: user?.email || '',
                    permissions: ['all']
                  }}
                  currentRoute={location.pathname}
                  onNavigate={(route) => navigate(route)}
                  onLogout={handleLogout}
                />
              )}
            </>
          ) : (userType === 'user' || localStorage.getItem('user_type') === 'user') ? (
            // User V2 Routes (Permission-based access)
            <>
              {/* Inbox routes (Open RFQs and Finalized RFQs) - replaces Open RFQ and Closed RFQ */}
              {user?.permissions?.open_rfq_request && location.pathname === '/dashboard/user/inbox/open' && (
                <InboxDashboard
                  key="inbox-open" // Unique key to ensure separate component instance
                  user={{
                    id: user?._id || '',
                    _id: user?._id || '',
                    role: 'user',
                    name: user?.name || 'User',
                    email: user?.email || '',
                    company: user?.company || '',
                    company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                    permissions: user?.permissions || {}
                  }}
                  currentRoute={location.pathname}
                  onNavigate={(route: string) => navigate(route)}
                  onLogout={handleLogout}
                  phaseFilter="open"
                />
              )}
              {user?.permissions?.open_rfq_request && location.pathname === '/dashboard/user/inbox/finalized' && (
                <InboxDashboard
                  key="inbox-finalized" // Unique key to ensure separate component instance
                  user={{
                    id: user?._id || '',
                    _id: user?._id || '',
                    role: 'user',
                    name: user?.name || 'User',
                    email: user?.email || '',
                    company: user?.company || '',
                    company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                    permissions: user?.permissions || {}
                  }}
                  currentRoute={location.pathname}
                  onNavigate={(route: string) => navigate(route)}
                  onLogout={handleLogout}
                  phaseFilter="finalized"
                />
              )}
              {/* Legacy routes - redirect to new inbox routes */}
              {user?.permissions?.open_rfq_request && location.pathname === '/dashboard/user/open-rfq' && (
                <Navigate to="/dashboard/user/inbox/open" replace />
              )}
              {user?.permissions?.open_rfq_request && location.pathname === '/dashboard/user/closed-rfq' && (
                <Navigate to="/dashboard/user/inbox/finalized" replace />
              )}
              {user?.permissions?.open_rfq_request && location.pathname === '/dashboard/user/create-request/marketplace' && (
                <CreateRequestDashboard
                  user={{
                    id: user?._id || '',
                    _id: user?._id || '',
                    role: 'user',
                    name: user?.name || 'User',
                    email: user?.email || '',
                    company: user?.company || '',
                    company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                    permissions: user?.permissions || {}
                  }}
                  currentRoute={location.pathname}
                  onNavigate={(route: string) => navigate(route)}
                  onLogout={handleLogout}
                />
              )}
              {user?.permissions?.open_rfq_request && location.pathname === '/dashboard/user/create-request/manual' && (
                <ManualProductDashboard
                  user={{
                    id: user?._id || '',
                    _id: user?._id || '',
                    role: 'user',
                    name: user?.name || 'User',
                    email: user?.email || '',
                    company: user?.company || '',
                    company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                    permissions: user?.permissions || {}
                  }}
                  currentRoute={location.pathname}
                  onNavigate={(route: string) => navigate(route)}
                  onLogout={handleLogout}
                />
              )}
              {/* Inbox and Settings - always accessible (default) */}
              {location.pathname === '/dashboard/user/inbox' && (
                <InboxDashboard
                  user={{
                    id: user?._id || '',
                    _id: user?._id || '',
                    role: 'user',
                    name: user?.name || 'User',
                    email: user?.email || '',
                    company: user?.company || '',
                    company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                    permissions: user?.permissions || {}
                  }}
                  currentRoute={location.pathname}
                  onNavigate={(route: string) => navigate(route)}
                  onLogout={handleLogout}
                />
              )}
              {location.pathname === '/dashboard/user/settings' && (
                <SettingsDashboard
                  user={{
                    id: user?._id || '',
                    _id: user?._id || '',
                    role: user?.role || 'user', // Use actual role ID instead of hardcoded 'user'
                    name: user?.name || 'User',
                    email: user?.email || '',
                    company: user?.company || '',
                    company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                    permissions: user?.permissions || {},
                    is_admin: user?.is_admin || false
                  }}
                  currentRoute={location.pathname}
                  onNavigate={(route: string) => navigate(route)}
                  onLogout={handleLogout}
                />
              )}
              {user?.permissions?.open_rfq_request && location.pathname === '/dashboard/user/cart' && (
                <CartPage />
              )}

              {/* Basic plan user RFQ Detail - UserLayout with 2nd sidebar for /dashboard/user/rfq/{id} */}
              {location.pathname.startsWith('/dashboard/user/rfq/') &&
                location.pathname !== '/dashboard/user/rfq' &&
                !location.pathname.includes('/products') &&
                !location.pathname.includes('/manual-products') &&
                !location.pathname.includes('/quotes-review') &&
                !location.pathname.includes('/suppliers') &&
                !location.pathname.includes('/communication') && (
                  <UserRFQManualProductsDashboard
                    user={{
                      id: user?._id || '',
                      _id: user?._id || '',
                      name: user?.name || 'User',
                      email: user?.email || '',
                      role: 'user' as any,
                      permissions: user?.permissions || {},
                      company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                      company: user?.company || ''
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                )}
              {/* Basic plan user RFQ sub-pages - Manual Products, etc. */}
              {location.pathname.startsWith('/dashboard/user/rfq/') &&
                location.pathname !== '/dashboard/user/rfq' &&
                (location.pathname.includes('/manual-products') ||
                  location.pathname.includes('/quotes-review') ||
                  location.pathname.includes('/suppliers') ||
                  location.pathname.includes('/communication')) && (
                  location.pathname.match(/\/dashboard\/user\/rfq\/[^/]+\/manual-products$/) ? (
                    <UserRFQManualProductsDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'user' as any,
                        permissions: user?.permissions || {},
                        company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                        company: user?.company || ''
                      }}
                      currentRoute={location.pathname}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  ) : location.pathname.match(/\/dashboard\/user\/rfq\/[^/]+\/quotes-review$/) ? (
                    <UserRFQQuotesReviewDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'user' as any,
                        permissions: user?.permissions || {},
                        company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                        company: user?.company || ''
                      }}
                      currentRoute={location.pathname}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  ) : location.pathname.match(/\/dashboard\/user\/rfq\/[^/]+\/communication(?:\/[^/]+)?$/) ? (
                    <UserRFQCommunicationDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'user' as any,
                        permissions: user?.permissions || {},
                        company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                        company: user?.company || ''
                      }}
                      currentRoute={location.pathname}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  ) : location.pathname.match(/\/dashboard\/user\/rfq\/[^/]+\/suppliers/) ? (
                    <UserRFQSupplierSearchDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'user' as any,
                        permissions: user?.permissions || {},
                        company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                        company: user?.company || ''
                      }}
                      currentRoute={location.pathname}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  ) : (
                    <RFQDetailDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'user' as any,
                        permissions: user?.permissions || {},
                        company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                        company: user?.company || ''
                      }}
                      currentRoute={location.pathname}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                      isReadOnly={false} // Basic plan users can edit but use UserLayout
                    />
                  )
                )}
              {/* Regular user RFQ Detail - for all /dashboard/rfqs/open/{id} routes (both with and without approve_ability) */}
              {location.pathname.startsWith('/dashboard/rfqs/open/') &&
                location.pathname !== '/dashboard/rfqs/open' &&
                !location.pathname.includes('/products') &&
                !location.pathname.includes('/manual-products') &&
                !location.pathname.includes('/quotes-review') &&
                !location.pathname.includes('/suppliers') &&
                !location.pathname.includes('/communication') && (
                  // Check if user is system admin
                  ((userType as any) === 'admin' || user?.is_admin || (user?.role as any) === 'admin') ? (
                    <RFQDetailDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'admin',
                        permissions: user?.permissions || {},
                        company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                        company: user?.company || ''
                      }}
                      currentRoute={location.pathname}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                      isReadOnly={false}
                    />
                  ) : (
                    // For regular users (including key users, test roles, etc.), show the interactive dashboard
                    <UserRFQManualProductsDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'user' as any,
                        permissions: user?.permissions || {},
                        company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                        company: user?.company || ''
                      }}
                      currentRoute={location.pathname}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  )
                )}
              {/* User company management routes - permission-based access */}
              {location.pathname.startsWith('/dashboard/companies/') && location.pathname !== '/dashboard/companies' && location.pathname !== '/dashboard/customers' ? (
                location.pathname.endsWith('/products') && user?.permissions?.edit_product ? (
                  <CompanyProductsDashboard
                    user={{
                      id: user?._id || '',
                      _id: user?._id || '',
                      name: user?.name || 'User',
                      email: user?.email || '',
                      role: 'useradmin',
                      company: user?.company || '',
                      company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                      permissions: user?.permissions || {}
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.endsWith('/product-details') && user?.permissions?.edit_product ? (
                  <CompanyProductDetailsDashboard
                    user={{
                      id: user?._id || '',
                      _id: user?._id || '',
                      name: user?.name || 'User',
                      email: user?.email || '',
                      role: 'useradmin',
                      company: user?.company || '',
                      company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                      permissions: user?.permissions || {}
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.endsWith('/suppliers') && user?.permissions?.edit_supplier ? (
                  <CompanySuppliersDashboard
                    user={{
                      id: user?._id || '',
                      _id: user?._id || '',
                      name: user?.name || 'User',
                      email: user?.email || '',
                      role: 'useradmin',
                      company: user?.company || '',
                      company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                      permissions: user?.permissions || {}
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.endsWith('/users') && user?.permissions?.user_rights ? (
                  <CompanyUsersDashboard
                    user={{
                      id: user?._id || '',
                      _id: user?._id || '',
                      name: user?.name || 'User',
                      email: user?.email || '',
                      role: 'useradmin',
                      company: user?.company || '',
                      company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                      permissions: user?.permissions || {}
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.endsWith('/user-rights') && user?.permissions?.user_rights ? (
                  <CompanyUserRightsDashboard
                    user={{
                      id: user?._id || '',
                      _id: user?._id || '',
                      name: user?.name || 'User',
                      email: user?.email || '',
                      role: 'useradmin',
                      company: user?.company || '',
                      company_id: user?.company_id || (typeof user?.company === 'string' ? user?.company : user?.company?._id) || '',
                      permissions: user?.permissions || {}
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : null
              ) : null}
            </>
          ) : null}
        </>
      ) : (
        /* Old Routes - Keep old wrapper */
        <main className="main-content">
          <div className="dashboard-container">
            {currentView === 'sent-emails' ? (
              <div>
                <div className="back-button-container">
                  <button onClick={handleBackToMain} className="back-btn">
                    {t('common.back_to_dashboard')}
                  </button>
                </div>
                <SentEmailsDashboard />
              </div>
            ) : (
              <>
                {(userType === 'admin' || localStorage.getItem('user_type') === 'admin') ? (
                  <AdminDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management']
                    }}
                    currentRoute={location.pathname}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : (userType === 'useradmin' || localStorage.getItem('user_type') === 'useradmin') ? (
                  <>
                    <StatsCards />
                    <div className="charts-section">
                      <YearlyOverview />
                      <SavingsChart />
                    </div>
                    <UserAdminProcurementTable />
                  </>
                ) : (
                  <>
                    <StatsCards />
                    <div className="charts-section">
                      <YearlyOverview />
                      <SavingsChart />
                    </div>
                    <ProcurementSection />
                  </>
                )}
              </>
            )}
          </div>
        </main>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={showNotificationModal}
        onClose={() => setShowNotificationModal(false)}
        onRevisionClick={handleRevisionClick}
      />

      {/* Revision Modal */}
      <NewRequestModal
        isOpen={showRevisionModal}
        onClose={handleRevisionModalClose}
        onSubmit={handleRevisionSubmit}
        editingRequest={revisionRequestData}
        isSubmitting={false}
      />

      {/* New Request Modal (for header button) */}
      <NewRequestModal
        isOpen={showNewRequestModal}
        onClose={handleCloseNewRequestModal}
        onSubmit={handleSubmitNewRequest}
        editingRequest={null}
        isSubmitting={false}
      />

      {/* Token Expired Modal */}
      <TokenExpiredModal
        isOpen={showTokenExpiredModal}
        onClose={() => setShowTokenExpiredModal(false)}
        onLogin={handleTokenExpiredLogin}
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <TranslationProvider>
        <AuthProvider>
          <SocketProvider>
            <CurrencyProvider>
              <Routes>
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/general-terms" element={<GeneralTerms />} />
                <Route path="/auth/sso-callback" element={<SsoCallbackPage />} />
                <Route path="/signin" element={<AppContent />} />
                <Route path="/user-page" element={<AppContent />} />
                <Route path="/admin-page" element={<AppContent />} />
                <Route path="/dashboard" element={<AppContent />} />
                <Route path="/dashboard/user/inbox/open" element={<AppContent />} />
                <Route path="/dashboard/user/inbox/finalized" element={<AppContent />} />
                {/* Legacy routes - redirect to new inbox routes */}
                <Route path="/dashboard/user/open-rfq" element={<Navigate to="/dashboard/user/inbox/open" replace />} />
                <Route path="/dashboard/user/closed-rfq" element={<Navigate to="/dashboard/user/inbox/finalized" replace />} />
                <Route path="/dashboard/user/create-request/marketplace" element={<AppContent />} />
                <Route path="/dashboard/user/create-request/manual" element={<AppContent />} />
                <Route path="/dashboard/user/inbox" element={<AppContent />} />
                <Route path="/dashboard/user/settings" element={<AppContent />} />
                {/* Legacy useradmin routes removed - use standard dashboard routes */}
                <Route path="/admin/request/:requestId" element={<RequestDetailPage />} />
                {/* Basic plan user routes - UserLayout with 2nd sidebar */}
                <Route path="/dashboard/user/rfq/:requestId/communication/:supplierId?" element={<AppContent />} />
                <Route path="/dashboard/user/rfq/:requestId/suppliers/:productId" element={<AppContent />} />
                <Route path="/dashboard/user/rfq/:requestId/*" element={<AppContent />} />
                <Route path="/dashboard/user/rfq/:requestId" element={<AppContent />} />
                <Route path="/dashboard/rfqs/open" element={<AppContent />} />
                <Route path="/dashboard/rfqs/open/:requestId" element={<AppContent />} />
                <Route path="/dashboard/rfqs/open/:requestId/communication/:supplierId?" element={<AppContent />} />
                <Route path="/dashboard/inbox" element={<AppContent />} />
                <Route path="/dashboard/inbox/old" element={<AppContent />} />
                <Route path="/dashboard/user/approve-rfq/:requestId" element={<AppContent />} />
                <Route path="/dashboard/rfqs/open/:requestId/suppliers/:productId" element={<AppContent />} />
                <Route path="/dashboard/companies/:companyId" element={<AppContent />} />
                <Route path="/dashboard/companies/:companyId/users" element={<AppContent />} />
                <Route path="/dashboard/companies/:companyId/products" element={<AppContent />} />
                <Route path="/dashboard/companies/:companyId/product-details" element={<AppContent />} />
                <Route path="/dashboard/companies/:companyId/suppliers" element={<AppContent />} />
                <Route path="/dashboard/companies/:companyId/user-rights" element={<AppContent />} />
                <Route path="/test/resend" element={<ResendTestPage />} />
                <Route path="/test/zepto" element={<ZeptoTestPage />} />
                <Route path="/" element={<AppContent />} />
                <Route path="*" element={<AppContent />} />
              </Routes>
            </CurrencyProvider>
          </SocketProvider>
        </AuthProvider>
      </TranslationProvider>
    </Router>
  );
}

export default App;
